import React from "react";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger" | "warning" | "success" | "link";
export type ButtonRole = "action" | "navigation" | "toolbar";
export type ButtonSize = "xs" | "sm" | "md" | "lg" | "icon";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  role?: ButtonRole;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  active?: boolean;
  selected?: boolean;
}

export function Button({
  variant = "primary",
  role = "action",
  size = "md",
  isLoading = false,
  active = false,
  selected = false,
  leftIcon,
  rightIcon,
  className = "",
  children,
  disabled,
  ...props
}: ButtonProps) {
  // Base states handling: Default, Hover, Pressed (active:), Focused (focus:), Disabled (disabled:)
  const baseStyles = "inline-flex items-center justify-center font-medium tracking-wide transition-all duration-300 ease-out rounded-xl cursor-pointer disabled:shadow-none disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-offset-2 border border-transparent";
  
  // Role modifiers (structural)
  const roleStyles = {
    action: "shadow-sm hover:shadow",
    navigation: "justify-start text-left w-full rounded-2xl",
    toolbar: "shadow-none"
  };

  const variants = {
    primary: "bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500 active:from-cyan-600 active:to-blue-700 active:scale-[0.98] disabled:from-slate-100 disabled:to-slate-100 disabled:text-slate-400 focus:ring-cyan-500/50",
    secondary: "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-blue-900 hover:border-slate-300 active:bg-slate-100 active:text-blue-900 active:scale-[0.98] disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200 focus:ring-slate-400/50",
    outline: "bg-transparent border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 active:bg-slate-100 active:scale-[0.98] disabled:border-slate-200 disabled:text-slate-400 focus:ring-slate-400/50",
    ghost: "bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200 active:text-slate-900 active:scale-[0.98] disabled:bg-transparent disabled:text-slate-400 focus:ring-slate-400/50",
    danger: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 active:scale-[0.98] disabled:bg-slate-100 disabled:text-slate-400 focus:ring-red-500/50",
    warning: "bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700 active:scale-[0.98] disabled:bg-slate-100 disabled:text-slate-400 focus:ring-amber-500/50",
    success: "bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700 active:scale-[0.98] disabled:bg-slate-100 disabled:text-slate-400 focus:ring-emerald-500/50",
    link: "bg-transparent text-blue-600 hover:underline hover:text-blue-800 active:text-blue-900 active:scale-[0.98] disabled:text-slate-400 focus:ring-blue-500/50 shadow-none !px-0 !py-0",
  };

  // Explicit Active / Selected state overrides by variant
  const activeStylesOverrides = {
    primary: "!bg-gradient-to-r !from-cyan-600 !to-blue-700 !shadow-inner",
    secondary: "!bg-slate-100 !text-blue-900 !border-slate-300",
    outline: "!bg-slate-100 !border-slate-400",
    ghost: role === 'navigation' ? "!bg-blue-50 !text-blue-700 font-bold" : "!bg-slate-200 !text-slate-900",
    danger: "!bg-red-800",
    warning: "!bg-amber-700",
    success: "!bg-emerald-700",
    link: "!text-blue-900 !font-bold",
  };

  const sizes = {
    xs: "text-[10px] px-3 py-1.5",
    sm: "text-[10px] px-4 py-2",
    md: "text-xs px-6 py-3",
    lg: "text-sm px-8 py-4",
    icon: "p-2 rounded-full",
  };

  const isActuallyActive = active || selected;

  return (
    <button
      className={`${baseStyles} ${roleStyles[role]} ${variants[variant]} ${sizes[size]} ${isActuallyActive ? activeStylesOverrides[variant] : ''} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <span className="material-symbols-outlined animate-spin mr-2 text-[1em]">progress_activity</span>
      ) : leftIcon ? (
        <span className={`inline-flex items-center justify-center ${children ? 'mr-2' : ''}`}>
          {typeof leftIcon === 'string' ? (
            <span className="material-symbols-outlined normal-case text-[1.2em] leading-none">{leftIcon.toLowerCase()}</span>
          ) : (
            leftIcon
          )}
        </span>
      ) : null}
      
      {children}

      {!isLoading && rightIcon && (
        <span className={`inline-flex items-center justify-center ${children ? 'ml-2' : ''}`}>
          {typeof rightIcon === 'string' ? (
            <span className="material-symbols-outlined normal-case text-[1.2em] leading-none">{rightIcon.toLowerCase()}</span>
          ) : (
            rightIcon
          )}
        </span>
      )}
    </button>
  );
}
